import type { ReactNode } from "react";
import { customerRefundPolicy } from "../shared/customer-copy";
import { LEGAL_EFFECTIVE_DATE } from "../shared/legal";

export const marketingPaths = [
  "/",
  "/how-it-works",
  "/pricing",
  "/sample-offer",
  "/faq",
  "/privacy",
  "/terms",
] as const;

export type MarketingPath = (typeof marketingPaths)[number];

export interface MarketingMetadata {
  title: string;
  description: string;
  eyebrow: string;
}

export const marketingMetadata = {
  "/": {
    title: "Car Buying Concierge & Dealer Negotiation | The Buying Lane",
    description:
      "The Buying Lane calls dealerships, verifies new-car availability, and brings you written out-the-door offers for one clear $500 service price.",
    eyebrow: "Your car-buying concierge",
  },
  "/how-it-works": {
    title: "How Our Car Buying Concierge Works | The Buying Lane",
    description:
      "See how The Buying Lane turns your vehicle requirements and price limit into current vehicle research and written out-the-door offers.",
    eyebrow: "How it works",
  },
  "/pricing": {
    title: "$500 Car Negotiation Service Pricing | The Buying Lane",
    description:
      "See The Buying Lane's $500 service pricing, two $250 checkpoints, and fixed 168-hour refund option for searches without a qualifying written offer.",
    eyebrow: "Simple pricing",
  },
  "/sample-offer": {
    title: "Sample Written Out-the-Door Car Offer | The Buying Lane",
    description:
      "See the vehicle identity, itemized price lines, conditions, and written out-the-door total The Buying Lane organizes for comparison.",
    eyebrow: "Sample written offer",
  },
  "/faq": {
    title: "Car Buying Concierge FAQ | The Buying Lane",
    description:
      "Answers about The Buying Lane's new-car concierge service, dealership calls, written offers, pricing, refunds, financing, and vehicle selection.",
    eyebrow: "Questions, answered",
  },
  "/privacy": {
    title: "Privacy Policy | The Buying Lane",
    description:
      "Read how The Buying Lane collects, uses, and shares information to provide and protect its car-buying concierge service.",
    eyebrow: "Privacy policy",
  },
  "/terms": {
    title: "Terms of Service | The Buying Lane",
    description:
      "Read the terms for The Buying Lane's supervised car-buying concierge service, secure payments, and seven-day refund option.",
    eyebrow: "Terms of service",
  },
} satisfies Record<MarketingPath, MarketingMetadata>;

const marketingPathSet: ReadonlySet<string> = new Set(marketingPaths);

export function isMarketingPath(pathname: string): pathname is MarketingPath {
  return marketingPathSet.has(pathname);
}

type RoadbookPhotoName = "headlight" | "night-road" | "rear-quarter";

const roadbookPhotoAssets = {
  headlight: {
    small: "/assets/roadbook-headlight-960.webp",
    large: "/assets/roadbook-headlight.webp",
  },
  "night-road": {
    small: "/assets/roadbook-night-road-960.webp",
    large: "/assets/roadbook-night-road.webp",
  },
  "rear-quarter": {
    small: "/assets/roadbook-rear-quarter-960.webp",
    large: "/assets/roadbook-rear-quarter.webp",
  },
} satisfies Record<RoadbookPhotoName, { small: string; large: string }>;

export function RoadbookPhoto({
  alt,
  className = "",
  loading = "lazy",
  photo,
}: {
  alt: string;
  className?: string;
  loading?: "eager" | "lazy";
  photo: RoadbookPhotoName;
}) {
  const asset = roadbookPhotoAssets[photo];
  return (
    <picture className={`roadbook-photo ${className}`.trim()}>
      <source media="(max-width: 720px)" srcSet={asset.small} />
      <img
        src={asset.large}
        width="1600"
        height="901"
        loading={loading}
        decoding="async"
        alt={alt}
      />
    </picture>
  );
}

function Brand({ inverse = false }: { inverse?: boolean }) {
  return (
    <a className={`brand ${inverse ? "brand--inverse" : ""}`} href="/">
      <span className="brand__mark" aria-hidden="true">
        B
      </span>
      <span>
        The Buying
        <br />
        Lane
      </span>
    </a>
  );
}

function CtaLink({
  children,
  href,
  tone = "primary",
  analyticsId,
}: {
  children: ReactNode;
  href: string;
  tone?: "primary" | "secondary" | "quiet";
  analyticsId: string;
}) {
  return (
    <a
      className={`button button--${tone}`}
      href={href}
      data-analytics-id={analyticsId}
    >
      {children}
    </a>
  );
}

const navigationItems = [
  ["/how-it-works", "How it works"],
  ["/pricing", "Pricing"],
  ["/sample-offer", "Sample offer"],
  ["/faq", "FAQ"],
] as const;

function MarketingHeader({
  activePath,
  overlay = false,
}: {
  activePath: MarketingPath;
  overlay?: boolean;
}) {
  const navigation = (
    <>
      {navigationItems.map(([href, label]) => (
        <a
          aria-current={activePath === href ? "page" : undefined}
          href={href}
          key={href}
        >
          {label}
        </a>
      ))}
      <a href="/login">Sign in</a>
      <CtaLink href="/start" analyticsId="nav_check_fit">
        Check your fit
      </CtaLink>
    </>
  );

  return (
    <header
      className={`marketing-header roadbook-header ${
        overlay
          ? "marketing-header--overlay roadbook-header--overlay"
          : "marketing-header--solid"
      }`}
    >
      <Brand inverse={overlay} />
      <nav className="marketing-nav marketing-nav--desktop" aria-label="Primary">
        {navigation}
      </nav>
      <details
        className="marketing-nav marketing-nav--mobile"
        onClick={(event) => {
          if (event.target instanceof Element && event.target.closest("a")) {
            event.currentTarget.removeAttribute("open");
          }
        }}
      >
        <summary>Menu</summary>
        <nav aria-label="Mobile primary">{navigation}</nav>
      </details>
    </header>
  );
}

function MarketingFooter() {
  return (
    <footer className="marketing-footer">
      <div className="marketing-footer__identity">
        <Brand />
        <p>
          A car-buying concierge for new vehicles, written prices, and fewer
          dealership conversations.
        </p>
      </div>
      <nav aria-label="Footer">
        {navigationItems.map(([href, label]) => (
          <a href={href} key={href}>
            {label}
          </a>
        ))}
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
        <a href="/login">Customer sign in</a>
      </nav>
      <p className="marketing-footer__service-line">
        New vehicles · United States · $500 total service price
      </p>
    </footer>
  );
}

function ChapterHeader({
  body,
  eyebrow,
  id,
  number,
  title,
}: {
  body?: string;
  eyebrow: string;
  id?: string;
  number: string;
  title: string;
}) {
  return (
    <div className="roadbook-chapter-header">
      <span className="roadbook-chapter-header__number" aria-hidden="true">
        {number}
      </span>
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2 id={id}>{title}</h2>
        {body ? <p>{body}</p> : null}
      </div>
    </div>
  );
}

function ProofStrip() {
  return (
    <section className="roadbook-brief" aria-labelledby="brief-title">
      <div className="roadbook-brief__heading">
        <p className="eyebrow">The essentials</p>
        <h2 id="brief-title">A clearer route from want to written price.</h2>
      </div>
      <ol className="roadbook-brief__list">
        <li>
          <span>01</span>
          <div>
            <strong>Written out-the-door offers</strong>
            <p>Compare the total price and the lines each dealership supplied.</p>
          </div>
        </li>
        <li>
          <span>02</span>
          <div>
            <strong>Current vehicle research before the calls</strong>
            <p>We confirm the vehicle details that shape where we start.</p>
          </div>
        </li>
        <li>
          <span>03</span>
          <div>
            <strong>A fixed seven-day refund option</strong>
            <p>{customerRefundPolicy}</p>
          </div>
        </li>
      </ol>
    </section>
  );
}

function WrittenOffer({ detailed = false }: { detailed?: boolean }) {
  return (
    <article
      className={`written-offer ${detailed ? "written-offer--detailed" : ""}`.trim()}
      aria-label="Offer A written offer"
    >
      <header className="written-offer__header">
        <div>
          <span className="written-offer__status">Written offer</span>
          <strong>2026 compact SUV · XLE AWD</strong>
        </div>
        <span className="written-offer__label">Offer A</span>
      </header>
      <div className="written-offer__identity">
        <div>
          <span>Vehicle reference</span>
          <strong>VIN ending 4821</strong>
        </div>
        <div>
          <span>Offer timing</span>
          <strong>Valid through Aug. 14</strong>
        </div>
      </div>
      <dl>
        <div>
          <dt>Vehicle selling price</dt>
          <dd>$37,400</dd>
        </div>
        <div>
          <dt>Dealer / document fee</dt>
          <dd>$399</dd>
        </div>
        <div>
          <dt>Dealer add-ons</dt>
          <dd>None stated</dd>
        </div>
        <div>
          <dt>Delivery</dt>
          <dd>Not itemized</dd>
        </div>
        <div>
          <dt>Tax, title &amp; registration</dt>
          <dd>$4,021</dd>
        </div>
      </dl>
      <div className="written-offer__total">
        <span>Written out-the-door total</span>
        <strong>$41,820</strong>
      </div>
      {detailed ? (
        <footer>
          <strong>Dealer condition</strong>
          <p>
            No trade, down payment, financing credit, or conditional rebate included.
          </p>
        </footer>
      ) : null}
    </article>
  );
}

function OfferComparison() {
  return (
    <div
      className="offer-comparison"
      role="group"
      aria-label="Offer A written out-the-door comparison"
    >
      <div className="offer-comparison__cell offer-comparison__cell--label">
        <span>Offer label</span>
        <strong>Offer A</strong>
        <small>Written dealer offer</small>
      </div>
      <div className="offer-comparison__cell offer-comparison__cell--price">
        <span>Written out-the-door total</span>
        <strong>$41,820</strong>
        <small>Vehicle, stated fees, tax, title, and registration</small>
      </div>
      <div className="offer-comparison__cell offer-comparison__cell--conditions">
        <span>Conditions</span>
        <strong>Stay attached to the price</strong>
        <small>
          Valid through Aug. 14 · No trade, down payment, financing credit, or
          conditional rebate included.
        </small>
      </div>
    </div>
  );
}

function Journey() {
  return (
    <section className="roadbook-section journey-section" aria-labelledby="journey-title">
      <ChapterHeader
        body="You choose the car and approve your target price. We handle the research, dealership calls, and written offers for you to review."
        eyebrow="01 / The buying lane"
        id="journey-title"
        number="01"
        title="You set the destination. We handle the dealer runaround."
      />
      <div className="journey-grid">
        <article className="journey-step">
          <span className="journey-step__number">01</span>
          <p className="eyebrow">Build the search</p>
          <h3>Tell us what you want</h3>
          <p>
            Bring an exact vehicle or let us help narrow the field. Define what must
            match, what can flex, and the most you will pay out the door.
          </p>
          <dl className="search-preview">
            <div>
              <dt>Vehicle</dt>
              <dd>Exact or guided</dd>
            </div>
            <div>
              <dt>Price plan</dt>
              <dd>Target + price limit</dd>
            </div>
            <div>
              <dt>Timing</dt>
              <dd>Your buying window</dd>
            </div>
          </dl>
        </article>
        <article className="journey-step">
          <span className="journey-step__number">02</span>
          <p className="eyebrow">Take the calls off your plate</p>
          <h3>We contact dealerships</h3>
          <p>
            We confirm the vehicle, ask the price questions, and push past advertised
            payment talk to the complete written total.
          </p>
          <ul className="lane-checklist">
            <li>Confirm the exact VIN or stock number</li>
            <li>Request the out-the-door price in writing</li>
            <li>Capture fees, add-ons, conditions, and timing</li>
          </ul>
        </article>
        <article className="journey-step">
          <span className="journey-step__number">03</span>
          <p className="eyebrow">See the numbers clearly</p>
          <h3>Compare written offers</h3>
          <p>
            Review the same essential facts across dealerships, choose the offer that
            works for you, and ask us to make the introduction.
          </p>
          <div
            className="journey-mini-offer"
            role="group"
            aria-label="Offer A written out-the-door total"
          >
            <span>Offer A · Written offer</span>
            <strong aria-label="Written out-the-door total, $41,820">$41,820</strong>
            <small>
              Out-the-door total · vehicle, fees, tax, and stated conditions
            </small>
          </div>
        </article>
      </div>
    </section>
  );
}

function PricingLedger({
  compact = false,
  includeRefundAssurance = compact,
}: {
  compact?: boolean;
  includeRefundAssurance?: boolean;
}) {
  return (
    <section
      className={`roadbook-pricing ${compact ? "roadbook-pricing--compact" : ""}`.trim()}
      aria-labelledby={compact ? "home-pricing-title" : "pricing-ledger-title"}
    >
      <div className="roadbook-pricing__promise">
        <p className="eyebrow">03 / The price</p>
        <h2 id={compact ? "home-pricing-title" : "pricing-ledger-title"}>$500 total.</h2>
        <p>Two $250 payments. No subscription or percentage fee.</p>
        {includeRefundAssurance ? (
          <div className="roadbook-pricing__assurance">
            <span aria-hidden="true">↺</span>
            <p>
              <strong>No offer at your price?</strong> You get every service payment
              back.
            </p>
          </div>
        ) : null}
      </div>
      <div className="roadbook-receipt" aria-label="Two payment checkpoints">
        <ol>
          <li>
            <p className="eyebrow">First payment</p>
            <div className="roadbook-receipt__line">
              <h3>We start the search</h3>
              <strong className="roadbook-receipt__amount">$250</strong>
            </div>
            <p>
              This first payment starts the fixed 168-hour window once Stripe
              confirms it. It is due after you approve your car search and target price.
            </p>
          </li>
          <li>
            <p className="eyebrow">Second payment</p>
            <div className="roadbook-receipt__line">
              <h3>You choose an offer</h3>
              <strong className="roadbook-receipt__amount">$250</strong>
            </div>
            <p>Due when you choose a written offer and ask us to connect you.</p>
          </li>
        </ol>
        <div className="roadbook-receipt__total">
          <span>Total service price</span>
          <strong>$500</strong>
        </div>
      </div>
    </section>
  );
}

export const marketingFaqItems = [
  {
    question: "What does The Buying Lane do?",
    answer:
      "We help you choose the new vehicle you want and set a target price that feels right. Then we research matching vehicles, call dealerships for you, request written out-the-door offers, and organize them so you can compare.",
  },
  {
    question: "How does the seven-day refund option work?",
    answer: customerRefundPolicy,
  },
  {
    question: "Do I need to know the exact vehicle already?",
    answer:
      "No. You can start with a car you already have in mind or use our guided questions to narrow down the new vehicle that fits you. We confirm the vehicle you choose before we call dealerships.",
  },
  {
    question: "What is an out-the-door price?",
    answer:
      "An out-the-door price is the full price of the vehicle plus the fees, delivery, taxes, title, and registration the dealer states or itemizes. It is the complete purchase price, not a monthly payment.",
  },
  {
    question: "How do I compare written offers?",
    answer:
      "We show the written out-the-door price first, then the exact car, the costs the dealer lists, the conditions attached to the offer, and any missing items marked as not itemized. That lets you compare offers in the same clear format.",
  },
  {
    question: "When does a dealership receive my contact information?",
    answer:
      "Your contact information stays private while we research and collect offers. We share it only after you choose a written offer and ask us to connect you with the dealership.",
  },
  {
    question: "Can I buy with cash or financing?",
    answer:
      "Yes. You can buy with cash, use financing from another lender, or consider dealer financing. We include a rebate or financing credit only when it applies to you and the dealer states its conditions.",
  },
  {
    question: "Can you search outside my local area?",
    answer:
      "Yes. You choose the search range and whether pickup or delivery works for you. When a dealer supplies a delivery cost, we keep it visible in the written offer.",
  },
  {
    question: "How long does the process take?",
    answer:
      "Our goal is to bring you multiple written offers within your first seven days. The timing can vary with inventory and how quickly dealerships respond.",
  },
] as const;

function FaqList({ full = false }: { full?: boolean }) {
  const items = full ? marketingFaqItems : marketingFaqItems.slice(0, 4);
  return (
    <div className="faq-list">
      {items.map((item) => (
        <details key={item.question}>
          <summary>{item.question}</summary>
          <p>{item.answer}</p>
        </details>
      ))}
    </div>
  );
}

function FinalCta({
  photo = "rear-quarter",
  title = "Put the dealership calls in our lane.",
}: {
  photo?: RoadbookPhotoName;
  title?: string;
}) {
  return (
    <section className="marketing-final-cta">
      <div className="marketing-final-cta__copy">
        <p className="eyebrow">Next step</p>
        <h2>{title}</h2>
        <p>Start your car search with the vehicle you want and a price limit.</p>
        <CtaLink href="/start" analyticsId="final_check_fit">
          Check your fit&nbsp; →
        </CtaLink>
      </div>
      <figure className="marketing-final-cta__photo">
        <RoadbookPhoto
          photo={photo}
          alt="A car at a dealership after dark, ready for the next road."
          loading="lazy"
        />
      </figure>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <section className="hero marketing-hero">
        <div className="hero__content">
          <p className="eyebrow">A car-buying concierge on your side</p>
          <h1>Buy the car. Skip the dealer runaround.</h1>
          <p className="hero__lede">
            Tell us the new vehicle and your price limit. We research the field, call
            dealerships, and bring you written out-the-door offers to compare.
          </p>
          <div className="hero__actions">
            <CtaLink href="/start" analyticsId="hero_check_fit">
              Check your fit&nbsp; →
            </CtaLink>
            <CtaLink
              href="/sample-offer"
              tone="secondary"
              analyticsId="hero_sample_offer"
            >
              See a sample offer
            </CtaLink>
          </div>
          <p className="marketing-hero__service-line">
            New vehicles · US buyers · $500 total
          </p>
        </div>
      </section>
      <ProofStrip />
      <Journey />
      <section className="roadbook-section offer-preview" aria-labelledby="offer-preview-title">
        <ChapterHeader
          body="Every offer view leads with the written out-the-door total, then shows the exact vehicle, itemized amounts, conditions, and anything the dealership did not break out."
          eyebrow="02 / The receipt"
          id="offer-preview-title"
          number="02"
          title="A complete price beats another sales pitch."
        />
        <div className="offer-preview__body">
          <OfferComparison />
          <CtaLink
            href="/sample-offer"
            tone="secondary"
            analyticsId="home_offer_details"
          >
            Explore the sample&nbsp; →
          </CtaLink>
        </div>
      </section>
      <PricingLedger compact />
      <section className="roadbook-section faq-preview" aria-labelledby="faq-preview-title">
        <ChapterHeader
          eyebrow="04 / The fine print"
          id="faq-preview-title"
          number="04"
          title="The questions that matter, answered plainly."
        />
        <FaqList />
        <a className="text-link" href="/faq">
          Read every question&nbsp; →
        </a>
      </section>
      <FinalCta />
    </>
  );
}

function PageOpener({
  body,
  chapter,
  path,
  photo,
  photoAlt,
  title,
}: {
  body: string;
  chapter: string;
  path: MarketingPath;
  photo: RoadbookPhotoName;
  photoAlt: string;
  title: string;
}) {
  return (
    <section className="page-opener">
      <div className="page-opener__copy">
        <p className="eyebrow">{chapter} / {marketingMetadata[path].eyebrow}</p>
        <span className="page-opener__number" aria-hidden="true">{chapter}</span>
        <h1>{title}</h1>
        <p>{body}</p>
      </div>
      <figure className="page-opener__photo">
        <RoadbookPhoto photo={photo} alt={photoAlt} loading="eager" />
      </figure>
    </section>
  );
}

function HowItWorksPage() {
  const steps = [
    ["01", "Check the fit", "Answer three questions about your starting point, timing, and main goal."],
    ["02", "Confirm the search", "Define the exact vehicle or guided choice, must-match details, acceptable flexibility, location, pickup or delivery, and your editable target and price limit."],
    ["03", "Prepare the search", "We find matching new vehicles and prepare the vehicle details and open questions for the dealerships we contact."],
    ["04", "Call for the complete price", "We contact the approved dealerships, confirm the vehicle, work through the price questions, and request a VIN- or stock-specific out-the-door offer in writing."],
    ["05", "Compare and connect", "Your workspace organizes the written offers on the same essential facts. When one works for you, we connect you directly with that dealership and the written quote."],
  ] as const;
  return (
    <>
      <PageOpener
        path="/how-it-works"
        chapter="01"
        photo="night-road"
        photoAlt="A car following a wet mountain road at night."
        title="From car search to written dealer offer."
        body="One concierge service handles the research, dealership calls, and quote requests while your requirements and price limit stay fixed."
      />
      <section className="roadbook-section process-ledger" aria-labelledby="process-title">
        <ChapterHeader
          eyebrow="The route"
          id="process-title"
          number="01"
          title="Five stops. One accountable lane."
          body="The work stays in order so you can see what has happened, what comes next, and where your decision belongs."
        />
        <ol className="process-ledger__list">
          {steps.map(([number, title, body]) => (
            <li key={number}>
              <span>{number}</span>
              <div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>
      <section className="route-split" aria-labelledby="route-split-title">
        <h2 id="route-split-title" className="sr-only">Choose your starting point</h2>
        <div>
          <p className="eyebrow">If you know the car</p>
          <h3>Go straight to the configuration.</h3>
          <p>
            Start from year, make, and model, then identify what must match and what
            can flex across trim, drivetrain, color, equipment, location, and timing.
          </p>
        </div>
        <div>
          <p className="eyebrow">If you need help choosing</p>
          <h3>Narrow the field before the calls.</h3>
          <p>
            Use budget, passengers, cargo, towing, ownership plans, body style, fuel,
            must-have features, and what matters most to find a practical starting
            point.
          </p>
        </div>
      </section>
      <section className="roadbook-section written-standard" aria-labelledby="written-standard-title">
        <ChapterHeader
          eyebrow="What you receive"
          id="written-standard-title"
          number="02"
          title="We only bring you clear, written offers."
          body="You see the exact car, the full out-the-door price, the costs the dealer lists, and any conditions before you decide."
        />
        <ul>
          <li><strong>Exact vehicle</strong><span>VIN or dealer stock number tied to the offer</span></li>
          <li><strong>Complete total</strong><span>The dealer-stated out-the-door amount</span></li>
          <li><strong>Price lines</strong><span>Selling price, fees, add-ons, delivery, tax, and registration when supplied</span></li>
          <li><strong>Conditions</strong><span>Expiration, rebates, financing terms, deposits, or other stated requirements</span></li>
        </ul>
      </section>
      <FinalCta
        photo="night-road"
        title="Give your next car search one accountable lane."
      />
    </>
  );
}

function PricingPage() {
  return (
    <>
      <PageOpener
        path="/pricing"
        chapter="02"
        photo="headlight"
        photoAlt="The headlight and front quarter of a car outside a dealership at blue hour."
        title="One service. Two clear checkpoints."
        body="$500 total in two $250 payments. The first starts the search; the second is charged after you choose a written offer."
      />
      <PricingLedger />
      <section className="roadbook-section pricing-includes" aria-labelledby="pricing-includes-title">
        <ChapterHeader
          eyebrow="What the lane covers"
          id="pricing-includes-title"
          number="03"
          title="The work between “I want this car” and “put me in touch.”"
        />
        <ul>
          <li>Guided or exact-vehicle intake</li>
          <li>Current vehicle details checked before calls</li>
          <li>Dealership calls and structured follow-up</li>
          <li>Written out-the-door quote requests</li>
          <li>Offer organization and direct dealer introduction</li>
        </ul>
      </section>
      <section className="refund-feature" aria-labelledby="refund-title">
        <div>
          <p className="eyebrow">The seven-day option</p>
          <h2 id="refund-title">No qualifying written offer by the deadline puts the choice back with you.</h2>
        </div>
        <div>
          <p>{customerRefundPolicy}</p>
        </div>
      </section>
      <FinalCta photo="headlight" title="Start with a price you are ready to stand behind." />
    </>
  );
}

function SampleOfferPage() {
  return (
    <>
      <PageOpener
        path="/sample-offer"
        chapter="03"
        photo="rear-quarter"
        photoAlt="The rear quarter of a car glowing outside a dealership at night."
        title="See what a useful dealer offer looks like."
        body="A written out-the-door total is the anchor. Vehicle identity, itemization, conditions, and missing lines explain what that number actually contains."
      />
      <section className="roadbook-section sample-offer-layout" aria-labelledby="sample-offer-title">
        <div className="sample-offer-layout__intro">
          <ChapterHeader
            eyebrow="03 / Read the receipt"
            id="sample-offer-title"
            number="03"
            title="The total comes first. The details keep it honest."
            body="Use the same reading order for every written offer: identify the vehicle, find the total, then check the lines and conditions attached to it."
          />
        </div>
        <WrittenOffer detailed />
        <ol className="offer-annotations">
          <li><span>01</span><div><strong>The exact vehicle</strong><p>A VIN or stock number prevents one vehicle's price from being mistaken for another.</p></div></li>
          <li><span>02</span><div><strong>The written total</strong><p>The out-the-door amount leads the comparison instead of a monthly payment or conditional headline.</p></div></li>
          <li><span>03</span><div><strong>The supplied price lines</strong><p>Fees, add-ons, delivery, tax, and registration stay visible. A missing line is named rather than silently shown as zero.</p></div></li>
          <li><span>04</span><div><strong>The conditions</strong><p>Timing, rebates, financing, deposits, and other dealer-stated requirements stay attached to the price.</p></div></li>
        </ol>
      </section>
      <FinalCta title="Start with your car. End with numbers you can compare." />
    </>
  );
}

function FaqPage() {
  return (
    <>
      <PageOpener
        path="/faq"
        chapter="04"
        photo="night-road"
        photoAlt="A car finding its way along a dark road."
        title="Everything to know before we take the calls."
        body="Straight answers about the service, vehicle fit, current research, written offers, pricing, refunds, and the dealer connection."
      />
      <section className="roadbook-section faq-page" aria-labelledby="faq-page-title">
        <ChapterHeader
          eyebrow="04 / The fine print"
          id="faq-page-title"
          number="04"
          title="Keep the important questions in view."
          body="If your question is not here, your private workspace keeps the search details and next action together once you begin."
        />
        <FaqList full />
      </section>
      <FinalCta photo="night-road" title="Take the next turn when you are ready." />
    </>
  );
}

function PrivacyPage() {
  return (
    <>
      <PageOpener
        path="/privacy"
        chapter="05"
        photo="rear-quarter"
        photoAlt="A car outside a dealership at night."
        title="Privacy, in plain language."
        body={`This policy explains the information The Buying Lane uses to provide and protect the service. Effective ${LEGAL_EFFECTIVE_DATE}.`}
      />
      <section className="roadbook-section process-ledger" aria-labelledby="privacy-title">
        <ChapterHeader
          eyebrow="What happens with your information"
          id="privacy-title"
          number="05"
          title="The information needed to run your car search."
          body="We use customer information for the service, support, security, and the dealership work you authorize."
        />
        <ol className="process-ledger__list">
          <li>
            <span>01</span>
            <div>
              <h3>Information we collect</h3>
              <p>We collect account and contact details, vehicle preferences, location and delivery details, purchase and financing preferences, price limits, instructions, approvals, dealership communications, written offers, documents, and limited technical and first-party usage information.</p>
            </div>
          </li>
          <li>
            <span>02</span>
            <div>
              <h3>How we use it</h3>
              <p>We use information to operate and secure The Buying Lane, carry out the dealership work you authorize, process communications and documents, provide support, prevent abuse, and improve service reliability.</p>
            </div>
          </li>
          <li>
            <span>03</span>
            <div>
              <h3>When we share it</h3>
              <p>We share information as needed with dealerships you authorize, providers that help us operate the service, others you direct us to involve, and when disclosure is legally required.</p>
            </div>
          </li>
          <li>
            <span>04</span>
            <div>
              <h3>Payments</h3>
              <p>Stripe securely processes payments and refunds and provides us with payment status and transaction identifiers. We do not receive complete payment-card numbers.</p>
            </div>
          </li>
          <li>
            <span>05</span>
            <div>
              <h3>Automated tools</h3>
              <p>We may use service providers and automated tools, including AI-enabled tools, to operate and support the service.</p>
            </div>
          </li>
          <li>
            <span>06</span>
            <div>
              <h3>How we protect information</h3>
              <p>We use administrative, technical, and organizational safeguards designed to protect customer information. We keep information for as long as reasonably needed for the service, security, recordkeeping, and legal obligations.</p>
            </div>
          </li>
          <li>
            <span>07</span>
            <div>
              <h3>Your choices and questions</h3>
              <p>Email <a href="mailto:accounts@thebuyinglane.com">accounts@thebuyinglane.com</a>{" "}to ask a privacy question, update account information, or make a request concerning your information. We may verify your identity before acting on a request.</p>
            </div>
          </li>
        </ol>
      </section>
    </>
  );
}

function TermsPage() {
  return (
    <>
      <PageOpener
        path="/terms"
        chapter="06"
        photo="headlight"
        photoAlt="The front of a car outside a dealership at blue hour."
        title="The terms for your buying lane."
        body={`These terms cover The Buying Lane's supervised car-buying support service. Effective ${LEGAL_EFFECTIVE_DATE}.`}
      />
      <section className="roadbook-section process-ledger" aria-labelledby="terms-title">
        <ChapterHeader
          eyebrow="The service boundary"
          id="terms-title"
          number="06"
          title="You decide. We support the search."
          body="These terms keep your authority, our work, and the dealership's role clear."
        />
        <ol className="process-ledger__list">
          <li>
            <span>01</span>
            <div><h3>The service</h3><p>The Buying Lane researches new vehicles, contacts dealerships within your approved instructions, organizes verified written offers, and coordinates a dealership connection after you choose an offer.</p></div>
          </li>
          <li>
            <span>02</span>
            <div><h3>Your decisions</h3><p>You control your requirements, price limits, and offer decisions. We do not purchase a vehicle for you, accept an offer, approve financing, authorize a deposit or payment, sign documents, or make a vehicle-purchase decision for you.</p></div>
          </li>
          <li>
            <span>03</span>
            <div><h3>Automated tools</h3><p>We may use service providers and automated tools, including AI-enabled tools, in providing and supporting the service.</p></div>
          </li>
          <li>
            <span>04</span>
            <div><h3>Your account and responsibilities</h3><p>You must provide accurate information, protect your account, and use the service only for lawful personal vehicle-shopping purposes. You are responsible for reviewing your saved instructions and promptly telling us when they change.</p></div>
          </li>
          <li>
            <span>05</span>
            <div><h3>Authorized communications</h3><p>You authorize us to communicate with you and to contact dealerships and service providers as reasonably needed to perform the service within your approved instructions.</p></div>
          </li>
          <li>
            <span>06</span>
            <div><h3>Dealership terms</h3><p>Dealerships are independent third parties. Inventory, incentives, taxes, fees, financing, and offer terms may change. You must review the dealership's final documents before signing or paying.</p></div>
          </li>
          <li>
            <span>07</span>
            <div><h3>Fees and refunds</h3><p>{customerRefundPolicy}</p></div>
          </li>
          <li>
            <span>08</span>
            <div><h3>Payment processing</h3><p>Service payments are processed through secure Stripe Checkout. Eligible refunds are submitted to the original payment method.</p></div>
          </li>
          <li>
            <span>09</span>
            <div><h3>Service availability</h3><p>We do not guarantee that a particular vehicle, dealership response, offer, price, financing term, or savings result will be available. We may suspend or end access when needed to protect the service, comply with law, or address misuse.</p></div>
          </li>
          <li>
            <span>10</span>
            <div><h3>Ownership and permitted use</h3><p>The Buying Lane service, site, software, branding, and original content belong to us or our licensors. You may use them only for your personal use of the service and may not copy, interfere with, or misuse them.</p></div>
          </li>
          <li>
            <span>11</span>
            <div><h3>Liability</h3><p>To the fullest extent permitted by law, The Buying Lane is not liable for indirect, incidental, special, or consequential damages, or for a dealership's acts or omissions. Our aggregate liability arising from the service will not exceed the service fees you paid for the search at issue.</p></div>
          </li>
          <li>
            <span>12</span>
            <div><h3>Changes and contact</h3><p>We may update these terms by posting a revised version with a new effective date. Email <a href="mailto:accounts@thebuyinglane.com">accounts@thebuyinglane.com</a>{" "}with questions about these terms or your account.</p></div>
          </li>
        </ol>
      </section>
    </>
  );
}

export function MarketingNotFoundPage() {
  return (
    <div className="buyer-theme marketing-site">
      <MarketingHeader activePath="/" />
      <main id="main-content" className="marketing-not-found" tabIndex={-1}>
        <p className="eyebrow">404 · wrong turn</p>
        <h1>That road does not lead to a page.</h1>
        <p>Return to the main lane or check whether a private link is complete.</p>
        <CtaLink href="/" analyticsId="404_home">Return home</CtaLink>
      </main>
      <MarketingFooter />
    </div>
  );
}

export function MarketingPage({ pathname }: { pathname: MarketingPath }) {
  const content =
    pathname === "/"
      ? <HomePage />
      : pathname === "/how-it-works"
        ? <HowItWorksPage />
        : pathname === "/pricing"
          ? <PricingPage />
          : pathname === "/sample-offer"
            ? <SampleOfferPage />
            : pathname === "/faq"
              ? <FaqPage />
              : pathname === "/privacy"
                ? <PrivacyPage />
                : <TermsPage />;
  return (
    <div className="buyer-theme marketing-site">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <MarketingHeader activePath={pathname} overlay={pathname === "/"} />
      <main id="main-content" tabIndex={-1}>{content}</main>
      <MarketingFooter />
    </div>
  );
}
